import { useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { isFactorialIdUser } from 'modules/core/lib/factorialIdTokenRefresh'
import companiesCollection from 'modules/core/store/collections/companies'

import { buildRedirectUrl, selectCompany as fidSelectCompany } from 'modules/factorial_id/lib/api_client'
import { useFactorialIdCompanies } from 'modules/factorial_id/hooks/useFactorialIdCompanies'

/**
 * Custom hook that returns a company switch handler.
 *
 * When the current session was issued by Factorial ID (detected via the
 * _factorial_id_data cookie), all company switches go through the FID
 * company-selection challenge — regardless of whether the target company has
 * the DEV_FACTORIAL_ID feature flag.  This is necessary because the session
 * itself is FID-managed; the monolith rpc path would not properly hand off a
 * FID-issued session.
 *
 * Falls back to the monolith rpc path only when:
 *   - the session is NOT FID-issued, OR
 *   - the FID API is unreachable / the company is not found in FID
 *
 * @returns Handler function that takes a company ID and can be passed directly to onChange
 *
 * @example
 * ```tsx
 * const switchCompany = useSwitchCompany()
 * return <SidebarHeader onChange={switchCompany} />
 * ```
 */
export const useSwitchCompany = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { email } = useCurrentSession()

  // Detected synchronously from the _factorial_id_data cookie: present means
  // this session was issued by Factorial ID.
  const isFidAuthenticated = isFactorialIdUser()

  // Prefetched on mount so the company list is ready at switch time with no
  // extra latency. Undefined when skipped, loading, or errored — in all those
  // cases the switch falls through to the monolith path.
  const { data: fidCompanies } = useFactorialIdCompanies({ skip: !isFidAuthenticated })

  return useCallback(
    async (companyId: string) => {
      if (isFidAuthenticated && email) {
        const fidCompany = fidCompanies?.find((c) => c.external_id === companyId)

        if (fidCompany) {
          try {
            const response = await fidSelectCompany(fidCompany.id)

            if (response.next) {
              // Shortcut for the redirect challenge, that just involves
              // navigating to another page and is guaranteed to not have
              // follow-up challenges. We perform the redirect here instead of
              // going to Factorial ID.
              if (response.next.type === 'redirect') {
                window.location.href = response.next.to
              } else {
                const redirectUrl = buildRedirectUrl(email, response)
                window.location.href = redirectUrl
              }
              return
            }

            if (response.success) {
              window.location.replace(window.location.origin)
              return
            }
          } catch {
            // selectCompany failed — fall through to monolith
          }
        }
      }

      // Monolith fallback: session is not FID-issued, company not found in
      // FID, or the FID company-selection call failed.
      const selectedCompany = companiesCollection.get(companyId)
      await selectedCompany?.rpc('select_current')

      const returnTo = location.state?.returnTo
      if (returnTo) {
        navigate(returnTo)
      } else {
        window.location.replace(window.location.origin)
      }
    },
    [isFidAuthenticated, email, fidCompanies, location, navigate]
  )
}
